export type BookCafeFeatureSlug =
  | "cinema"
  | "board-games"
  | "game-zone"
  | "birthday-dining"
  | "family-lounge"
  | "resident-events";

export type BookCafeAccent = "purple" | "blue" | "mint" | "yellow" | "orange";

interface BookCafeDetailMedia {
  image: {
    src: string;
    alt: string;
  };
  video: {
    title: string;
    embedSrc: string;
    externalSrc: string;
  };
}

export interface BookCafeFeature {
  slug: BookCafeFeatureSlug;
  eyebrow: string;
  title: string;
  pageTitle?: string;
  summary: string;
  description: string;
  accent: BookCafeAccent;
  image?: {
    src: string;
    alt: string;
  };
  highlights: string[];
  detailTitle: string;
  detailDescription: string;
  usageNotice?: string;
  detailSections: Array<{
    title: string;
    description?: string;
    items: string[];
    media?: BookCafeDetailMedia;
  }>;
}

export interface BookCafeBoardGame {
  name: string;
  description: string;
  players: string;
  playTime: string;
  age: string;
  image?: {
    src: string;
    alt: string;
  };
}

export const BOOK_CAFE_BOARD_GAMES: BookCafeBoardGame[] = [
  {
    name: "루미큐브 클래식",
    description: "숫자 타일을 같은 숫자 또는 연속된 숫자로 조합해 가장 먼저 모든 타일을 내려놓는 가족 전략 게임입니다.",
    players: "2~4명",
    playTime: "20분",
    age: "8세 이상",
    image: {
      src: "/images/hpoint-renewal/book-cafe/book-cafe-board-game-rummikub-classic.png",
      alt: "루미큐브 클래식 게임 상자",
    },
  },
  {
    name: "카지노칩 세트 300pcs",
    description: "포커와 홀덤 등 카드게임에서 점수를 표시할 때 사용하는 카드게임용 점수 칩 세트입니다.",
    players: "2명 이상",
    playTime: "5분",
    age: "18세 이상",
    image: {
      src: "/images/hpoint-renewal/book-cafe/book-cafe-board-game-casino-chip-set.png",
      alt: "카지노칩 세트 300pcs",
    },
  },
  {
    name: "다빈치 코드",
    description: "공개된 단서를 바탕으로 상대가 숨긴 숫자 타일을 추리하는 짧고 긴장감 있는 논리 게임입니다.",
    players: "2~4명",
    playTime: "15분",
    age: "7세 이상",
    image: {
      src: "/images/hpoint-renewal/book-cafe/book-cafe-board-game-davinci-code.png",
      alt: "다빈치 코드 게임 상자와 숫자 타일",
    },
  },
  {
    name: "원목 자석 체스",
    description: "서로 다른 움직임을 가진 기물을 운용해 상대의 왕을 체크메이트하는 고전 전략 게임입니다.",
    players: "2명",
    playTime: "10분",
    age: "6세 이상",
    image: {
      src: "/images/hpoint-renewal/book-cafe/book-cafe-board-game-wooden-magnetic-chess.png",
      alt: "원목 자석 체스 세트",
    },
  },
  {
    name: "할리갈리",
    description: "펼쳐진 카드에서 한 종류의 과일이 정확히 5개가 되는 순간 종을 치는 순발력 게임입니다.",
    players: "2~6명",
    playTime: "15분",
    age: "6세 이상",
    image: {
      src: "/images/hpoint-renewal/book-cafe/book-cafe-board-game-halli-galli.png",
      alt: "할리갈리 게임 상자와 종, 카드",
    },
  },
  {
    name: "할리갈리 컵스 딜럭스",
    description: "카드의 색 순서와 위치에 맞춰 다섯 색 컵을 빠르게 쌓거나 나열하고 종을 치는 게임입니다.",
    players: "2~4명",
    playTime: "15분",
    age: "6세 이상",
    image: {
      src: "/images/hpoint-renewal/book-cafe/book-cafe-board-game-halli-galli-cups-deluxe.png",
      alt: "할리갈리 컵스 딜럭스 게임 상자",
    },
  },
  {
    name: "프리미어 윷놀이 세트",
    description: "윷가락 결과에 따라 말을 이동하고 같은 편 말을 업거나 상대 말을 잡는 전통 놀이입니다.",
    players: "2명 또는 2팀 이상",
    playTime: "15분",
    age: "6세 이상",
    image: {
      src: "/images/hpoint-renewal/book-cafe/book-cafe-board-game-premier-yutnori.png",
      alt: "프리미어 윷놀이 세트",
    },
  },
  {
    name: "젠가 클래식",
    description: "나무 블록을 탑에서 하나씩 빼내 맨 위에 다시 쌓으며 균형을 겨루는 손재주 게임입니다.",
    players: "1명 이상",
    playTime: "10분",
    age: "6세 이상",
    image: {
      src: "/images/hpoint-renewal/book-cafe/book-cafe-board-game-jenga-classic.png",
      alt: "젠가 클래식 게임 상자와 블록",
    },
  },
  {
    name: "부루마불 클래식",
    description: "세계 여러 도시를 여행하며 땅과 건물을 사고 통행료와 자산을 관리하는 경제 보드게임입니다.",
    players: "2~4명",
    playTime: "약 50~90분",
    age: "7세 이상",
    image: {
      src: "/images/hpoint-renewal/book-cafe/book-cafe-board-game-blue-marble-classic.png",
      alt: "부루마불 클래식 게임판과 상자",
    },
  },
  {
    name: "카탄",
    description: "자원을 생산하고 거래해 도로와 마을, 도시를 확장하는 협상 중심의 전략 게임입니다.",
    players: "3~4명",
    playTime: "75분",
    age: "10세 이상",
    image: {
      src: "/images/hpoint-renewal/book-cafe/book-cafe-board-game-catan.png",
      alt: "카탄 보드게임",
    },
  },
  {
    name: "텔레스트레이션",
    description: "제시어를 그림으로 그리고 다음 사람이 그림을 보고 단어를 추측하는 유쾌한 파티 게임입니다.",
    players: "4~8명",
    playTime: "20분",
    age: "8세 이상",
    image: {
      src: "/images/hpoint-renewal/book-cafe/book-cafe-board-game-telestrations-party-pack.jpg",
      alt: "텔레스트레이션 게임 상자",
    },
  },
  {
    name: "오델로",
    description: "내 돌로 상대 돌을 가로, 세로, 대각선에서 감싸 뒤집는 간결하고 깊이 있는 2인 전략 게임입니다.",
    players: "2명",
    playTime: "약 15분",
    age: "8세 이상",
    image: {
      src: "/images/hpoint-renewal/book-cafe/book-cafe-board-game-othello.png",
      alt: "오델로 게임 상자와 보드",
    },
  },
  {
    name: "스플렌더",
    description: "보석 토큰을 모아 개발 카드를 구입하고 구매 능력을 확장해 점수를 얻는 전략 게임입니다.",
    players: "2~4명",
    playTime: "30분",
    age: "10세 이상",
    image: {
      src: "/images/hpoint-renewal/book-cafe/book-cafe-board-game-splendor.png",
      alt: "스플렌더 게임 상자",
    },
  },
  {
    name: "로보77",
    description: "숫자 카드를 한 장씩 내며 누적 합계를 이어가고 위험한 숫자를 피하는 계산 카드게임입니다.",
    players: "2~8명",
    playTime: "30분",
    age: "8세 이상",
    image: {
      src: "/images/hpoint-renewal/book-cafe/book-cafe-board-game-robo77.png",
      alt: "로보77 게임 상자와 카드",
    },
  },
  {
    name: "럭키보카",
    description: "주제에 맞는 영어 단어를 알파벳 타일로 만들며 목적지를 향해 가는 영어 단어 게임입니다.",
    players: "2~4명",
    playTime: "약 20분",
    age: "8세 이상",
    image: {
      src: "/images/hpoint-renewal/book-cafe/book-cafe-board-game-lucky-voca.png",
      alt: "럭키보카 게임 상자",
    },
  },
  {
    name: "주주팝 페이스체인지",
    description: "카드에 나온 눈과 입을 조합해 동물의 표정을 완성하는 관찰력과 순발력 게임입니다.",
    players: "1~4명",
    playTime: "약 10분",
    age: "3세 이상",
    image: {
      src: "/images/hpoint-renewal/book-cafe/book-cafe-board-game-juju-pop.webp",
      alt: "주주팝 페이스체인지 게임 상자",
    },
  },
  {
    name: "텀블링몽키",
    description: "야자나무 안의 원숭이가 떨어지지 않도록 색 막대를 조심스럽게 빼는 손기술 게임입니다.",
    players: "2~4명",
    playTime: "약 20~30분",
    age: "4세 이상",
    image: {
      src: "/images/hpoint-renewal/book-cafe/book-cafe-board-game-tumbling-monkey.png",
      alt: "텀블링몽키 게임 상자와 구성품",
    },
  },
  {
    name: "포켓몬 피카츄 룰렛 게임",
    description: "피카츄가 들어 있는 통에 스틱을 차례로 꽂으며 언제 튀어 오를지 모르는 긴장감을 즐기는 게임입니다.",
    players: "2명 이상",
    playTime: "5분",
    age: "3세 이상",
    image: {
      src: "/images/hpoint-renewal/book-cafe/book-cafe-board-game-pikachu-roulette.png",
      alt: "포켓몬 피카츄 룰렛 게임 세트",
    },
  },
  {
    name: "쿼리도",
    description: "내 말은 전진시키고 상대의 길에는 벽을 세우며 반대편 끝에 먼저 도착하는 추상 전략 게임입니다.",
    players: "2~4명",
    playTime: "15분",
    age: "8세 이상",
    image: {
      src: "/images/hpoint-renewal/book-cafe/book-cafe-board-game-quoridor.png",
      alt: "쿼리도 게임 상자",
    },
  },
  {
    name: "러시아워",
    description: "차량을 앞뒤로 움직여 빨간 자동차가 빠져나갈 길을 만드는 단계별 1인용 슬라이딩 퍼즐입니다.",
    players: "1명",
    playTime: "5분",
    age: "8세 이상",
    image: {
      src: "/images/hpoint-renewal/book-cafe/book-cafe-board-game-rush-hour.png",
      alt: "러시아워 게임 상자와 보드",
    },
  },
];

export const BOOK_CAFE_OVERVIEW_IMAGE = {
  src: "/images/hpoint-renewal/book-cafe/book-cafe-overview.webp",
  alt: "빈백 소파와 보드게임 테이블이 마련된 북카페 전체 모습",
};

export const BOOK_CAFE_FEATURES: BookCafeFeature[] = [
  {
    slug: "cinema",
    eyebrow: "WATCH",
    title: "#시네마",
    pageTitle: "#시네마",
    summary: "빔프로젝터와 대형 스크린으로 완성한 우리 단지의 작은 영화관",
    description: "OTT 영화와 애니메이션부터 월드컵·한국시리즈 같은 스포츠 경기까지 함께 즐겨 보세요.",
    accent: "purple",
    image: {
      src: "/images/hpoint-renewal/book-cafe/book-cafe-cinema-real-v2.webp",
      alt: "대형 스크린으로 영상을 감상하는 북카페",
    },
    highlights: ["빔프로젝터", "대형 스크린", "OTT"],
    detailTitle: "영화부터 스포츠까지, 함께 볼수록 더 즐겁게",
    detailDescription:
      "넷플릭스·Disney+ 등 OTT로 영화와 애니메이션을 감상하고, 월드컵과 한국시리즈 같은 스포츠 경기도 함께 즐길 수 있습니다. 개인 OTT 계정으로 로그인하고, 이용 후에는 개인정보 보호를 위해 반드시 로그아웃해 주세요.",
    detailSections: [
      {
        title: "이렇게 즐겨보세요",
        items: [
          "가족 영화와 애니메이션 감상",
          "좋아하는 야구·축구 경기 함께 보기",
          "손님과 식사 후 편안한 영상 관람",
        ],
      },
      {
        title: "이용 전 확인",
        items: [
          "넷플릭스·유튜브·Disney+ 등은 이용자가 보유한 개인 OTT 계정으로 로그인해 주세요.",
          "다음 이용자의 개인정보 보호를 위해 퇴실 전 모든 서비스에서 반드시 로그아웃해 주세요.",
          "공동 관람 행사는 콘텐츠별 상영 조건과 이용 권리를 확인한 뒤 별도로 안내합니다.",
        ],
      },
    ],
  },
  {
    slug: "board-games",
    eyebrow: "PLAY",
    title: "#보드게임",
    pageTitle: "#보드게임",
    summary: "취향과 인원에 맞춰 고르는 다양한 보드게임",
    description: "가벼운 파티 게임부터 오래 즐기는 전략 게임까지 다양하게 준비되어 있습니다.",
    accent: "blue",
    image: {
      src: "/images/hpoint-renewal/book-cafe/book-cafe-board-games-real-v1.webp",
      alt: "테이블에서 보드게임을 즐길 수 있는 북카페",
    },
    highlights: ["어린이 게임", "청소년 게임", "성인 게임"],
    detailTitle: "취향 따라 고르는 보드게임",
    detailDescription:
      "인기 있는 파티 게임부터 오래 사랑받아 온 클래식, 최신 전략 게임까지 연령별로 즐길 수 있도록 준비했습니다. 가족은 물론 친구들과 함께 취향과 인원에 맞는 게임으로 즐거운 시간을 만들어 보세요.",
    usageNotice:
      "게임 시작 전 구성품을 확인하고 음식과 음료는 게임판에서 떨어진 곳에 두어 주세요. 이용 후에는 모든 구성품을 원래 상자에 정리해 주세요.",
    detailSections: [
      {
        title: "유아 보드게임",
        description:
          "주주팝은 동물 표정을 빠르게 관찰하고 같은 표정을 찾아보는 게임입니다. 간단한 규칙으로 어린이와 보호자가 함께 가볍게 즐기기 좋아요.",
        items: [],
        media: {
          image: {
            src: "/images/hpoint-renewal/book-cafe/book-cafe-board-game-juju-pop.webp",
            alt: "동물 표정 맞추기 게임 주주팝 페이스 체인지 상자",
          },
          video: {
            title: "주주팝 페이스 체인지 게임 방법 영상",
            embedSrc: "https://www.youtube-nocookie.com/embed/ju3mIWyUw5M",
            externalSrc: "https://www.youtube.com/watch?v=ju3mIWyUw5M",
          },
        },
      },
      {
        title: "청소년 보드게임",
        description:
          "텔레스트레이션 파티팩은 그림과 단어를 번갈아 전달하며 예상하지 못한 결과를 함께 웃고 즐기는 파티 게임입니다. 여러 명이 모인 청소년 모임에 잘 어울려요.",
        items: [],
        media: {
          image: {
            src: "/images/hpoint-renewal/book-cafe/book-cafe-board-game-telestrations-party-pack.jpg",
            alt: "텔레스트레이션 파티팩 게임 상자",
          },
          video: {
            title: "텔레스트레이션 파티팩 게임 방법 영상",
            embedSrc: "https://www.youtube-nocookie.com/embed/NkE7BMWJOpY",
            externalSrc: "https://www.youtube.com/watch?v=NkE7BMWJOpY",
          },
        },
      },
      {
        title: "그 밖에 준비된 보드게임",
        items: [
          "유아용 · 럭키보카, 텀블링몽키, 피카츄 룰렛 등",
          "청소년용 · 로보77, 부루마불, 루미큐브 등",
          "성인용 · 윷놀이, 카지노칩 세트, 체스 등",
        ],
      },
    ],
  },
  {
    slug: "game-zone",
    eyebrow: "GAME",
    title: "#엔터테인먼트",
    pageTitle: "#엔터테인먼트",
    summary: "가볍게 즐기고, 함께할수록 더 신나는 플레이 타임",
    description: "오락실 게임부터 다트 대결까지, 가족과 친구들이 함께 웃고 경쟁하며 일상 속 유쾌한 시간을 즐길 수 있는 공간입니다.",
    accent: "mint",
    image: {
      src: "/images/hpoint-renewal/book-cafe/book-cafe-entertainment-real-v1.webp",
      alt: "전자 다트와 2인용 아케이드 게임기가 있는 북카페 엔터테인먼트",
    },
    highlights: ["아케이드 게임", "다트 챌린지", "민속놀이"],
    detailTitle: "함께할수록 웃음이 커지는 우리만의 플레이 매치",
    detailDescription:
      "오락실 게임부터 다트 대결까지, 가족과 친구들이 함께 웃고 경쟁하며 일상 속 유쾌한 시간을 즐길 수 있는 공간입니다.",
    detailSections: [
      {
        title: "준비된 게임",
        items: [
          "오락실 게임기 1대에 약 200개의 레트로 게임이 준비되어 있습니다.",
          "칩이 포함된 포커 게임 세트를 이용할 수 있습니다.",
          "전자 다트가 점수를 자동으로 계산해 편리하게 대결할 수 있습니다.",
        ],
      },
      {
        title: "안전하고 건전하게",
        items: [
          "포커 세트는 게임 용도로만 이용하며 현금성 내기와 환금성 상품을 건 내기는 금지합니다.",
          "다트를 던질 때에는 투척선 안쪽에 다른 사람이 들어오지 않도록 확인해 주세요.",
          "어린이는 보호자와 함께 기기와 다트를 안전하게 이용해 주세요.",
        ],
      },
    ],
  },
  {
    slug: "birthday-dining",
    eyebrow: "CELEBRATE",
    title: "#프라이빗 파티",
    pageTitle: "#프라이빗 파티",
    summary: "우리끼리 오붓하게, 특별한 날을 더 특별하게",
    description: "가족과 친구들이 편안하게 모여 생일과 기념일, 소소한 파티를 즐길 수 있는 프라이빗 공간입니다.",
    accent: "orange",
    image: {
      src: "/images/hpoint-renewal/book-cafe/book-cafe-birthday-luxury-v2.png",
      alt: "풍선과 케이크로 생일잔치를 준비한 북카페",
    },
    highlights: ["파티룸 대관", "아이와 함께하는 모임", "친구들과 홈파티"],
    detailTitle: "우리끼리 즐기는 프라이빗 파티",
    detailDescription:
      "가족과 친구들이 편안하게 모여 생일과 기념일, 소소한 파티를 즐길 수 있는 프라이빗 공간입니다.",
    detailSections: [
      {
        title: "이렇게 이용할 수 있어요",
        items: [
          "아이의 친구와 가족을 초대한 생일 파티",
          "방문한 손님과 함께하는 식사와 티타임",
          "배달 음식과 주류는 허용하며, 주류는 성인만 이용할 수 있습니다. 실내 조리는 금지됩니다.",
        ],
      },
      {
        title: "파티 안전 안내",
        items: [
          "생일 장식과 생일초는 사용할 수 있습니다.",
          "생일초 사용 중에는 화재에 각별히 주의하고, 불을 켠 자리를 비우지 마세요.",
          "조리기구와 휴대용 버너 등 화기를 이용한 조리는 금지합니다.",
        ],
      },
    ],
  },
  {
    slug: "family-lounge",
    eyebrow: "RELAX",
    title: "패밀리 플레이·릴랙스",
    summary: "조금 아쉬운 시간을 편안하게 채워주는 휴식 공간",
    description: "어른이 대화를 나누는 동안 아이도 곁에서 놀 수 있는 가족 친화 공간입니다.",
    accent: "yellow",
    image: {
      src: "/images/hpoint-renewal/book-cafe/book-cafe-friends-conversation-luxury-v1.png",
      alt: "빈백 소파에서 친구들과 대화하며 쉬는 북카페 라운지",
    },
    highlights: ["뒤풀이", "대화", "휴식"],
    detailTitle: "아이들은 즐겁게, 어른들은 여유롭게",
    detailDescription:
      "가족이나 지인과의 만남이 조금 더 이어졌으면 하는 날, 부담 없이 커피 한잔과 대화를 즐겨보세요.",
    detailSections: [
      {
        title: "공간 안내",
        items: [
          "1인용 빈백 소파 6개가 준비되어 있습니다.",
          "만 13세 이하 어린이는 보호자와 반드시 함께 이용해야 합니다.",
        ],
      },
      {
        title: "편안함을 지켜주세요",
        items: [
          "카펫과 빈백에는 음식물이 묻지 않도록 주의해 주세요.",
          "뛰거나 빈백 위에서 점프하는 행동은 삼가 주세요.",
          "퇴실 전 가구와 물품을 원래 위치에 정리해 주세요.",
        ],
      },
    ],
  },
  {
    slug: "resident-events",
    eyebrow: "TOGETHER",
    title: "주민 프로그램",
    summary: "같은 취향과 응원으로 이웃이 가까워지는 공동 행사",
    description: "스포츠 응원전과 어린이 영화관처럼 함께할수록 즐거운 프로그램을 준비합니다.",
    accent: "purple",
    image: {
      src: "/images/hpoint-renewal/book-cafe/book-cafe-sports.webp",
      alt: "대형 스크린으로 스포츠 경기를 함께 관람하는 북카페",
    },
    highlights: ["스포츠 응원", "어린이 영화관", "입주민 교류"],
    detailTitle: "우리 단지의 즐거움을 함께 만드는 시간",
    detailDescription:
      "북카페가 이웃을 만나고 새로운 추억을 만드는 단지 안 작은 문화 공간이 됩니다.",
    detailSections: [
      {
        title: "생각하고 있는 행사",
        items: [
          "한국시리즈 등 응원팀 경기와 치킨·음료를 함께 즐기는 관람 행사",
          "아이들과 함께 즐기는 어린이 영화관",
          "가족과 이웃이 참여하는 보드게임 모임",
        ],
      },
      {
        title: "행사 운영 안내",
        items: [
          "현재 소개된 행사는 예정 프로그램이며 일정과 신청 방법은 확정 후 별도로 안내합니다.",
          "영상 공동 관람은 콘텐츠별 상영 조건과 이용 권리를 확인한 행사만 진행합니다.",
          "행사별 정원, 보호자 동반 기준과 음식 제공 여부는 모집 안내에서 확인해 주세요.",
        ],
      },
    ],
  },
];

export const getBookCafeFeature = (slug?: string) =>
  BOOK_CAFE_FEATURES.find((feature) => feature.slug === slug);
